import React, {useState, useEffect} from "react";
import { 
  Input, 
  Label, 
  CheckBox, 
  FlexBox, 
  FlexBoxDirection, 
  RadioButton,
  ComboBox,
  ComboBoxItem,
  Button,
  Dialog
} from "@ui5/webcomponents-react";
import UIModel from "../models/UIModel.json";

// Reusable FormInput component
const FormInput = ({ label, value, onChange, placeholder, type = "text", style = {} }) => (
  <div style={{ marginBottom: "1rem" }}>
    <Label design="Bold">{label}</Label>
    <Input
      value={value}
      onInput={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      type={type}
      style={{ width: "100%", ...style }}
    />
  </div>
);

const SocioeconomicForm = ({ formData, onFormChange, handlePrevious, handleNext }) => {
  // Safely extract all data with fallbacks
  const socioeconomicData = UIModel?.socioeconomicForm || {};
  const { lotHouseOptions = [], internetProviderOptions = [], mobileProviderOptions = [] } = socioeconomicData;
  const [open, setOpen] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const [dialogField, setDialogField] = useState(null);
  const [dialogFieldOptions, setDialogFieldOptions] = useState(null);
  const [dialogIndex, setDialogIndex] = useState(null);
  const [appliancesOthers, setAppliancesOthers] = useState(formData.householdAppliancesOthers || {});
  const [ictOthers, setIctOthers] = useState(formData.ictDevicesOthers || {});
  const [vehiclesOthers, setVehiclesOthers] = useState(formData.vehiclesOthers || {});

  const handleDialogSubmit = (field, fieldOptions) => {
    setOpen(false);
    const newProviders = [...formData.mobileProvider];
    newProviders[dialogIndex] = customValue;
    onFormChange("mobileProvider", newProviders);
    setCustomValue("");
  };

  const addAssetOther = (assetSetter, assetOthers, assetItems) => {
    if (!assetOthers) {
      assetSetter({[assetItems.length + 1]: {name: '', quanitity: 0}})
    } else if (Object.keys(assetOthers).length >= 0) {
      assetSetter((prev) => ({...prev, [assetItems.length + Object.keys(assetOthers).length + 1]: {name: '', quantity: 0}}))
    } 
  }

  const deleteAssetOther = (assetSetter, assetOthers, assetItems) => {
    if (Object.keys(assetOthers).length !== 0) {
      assetSetter((prev) => {
        const { [assetItems.length + Object.keys(assetOthers).length]: removed, ...rest } = prev;
        return rest;
      })
    }
  }

  const onAssetOtherInputChange = (assetSetter, index, key, value) => {
    assetSetter(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [key]: value
      }
    }));
  };

  useEffect(() => {
    onFormChange("householdAppliancesOthers", appliancesOthers);
  }, [onFormChange, appliancesOthers]);

  useEffect(() => {
    onFormChange("ictDevicesOthers", ictOthers);
  }, [onFormChange, ictOthers]);

  useEffect(() => {
    onFormChange("vehiclesOthers", vehiclesOthers)
  }, [onFormChange, vehiclesOthers] )

  // Use static arrays for appliances, ICT, and vehicles as per backend model
  const applianceItems = [
    { number: 1, label: "Refrigerator" },
    { number: 2, label: "Washing Machine" },
    { number: 3, label: "Electric Fan" },
    { number: 4, label: "Air Conditioner" },
    { number: 5, label: "Rice Cooker" },
    { number: 6, label: "Microwave Oven" },
    { number: 7, label: "Gas Stove" },
    { number: 8, label: "Electric Stove" },
    { number: 9, label: "Blender" },
    { number: 10, label: "Water Dispenser" },
    { number: 11, label: "Vacuum Cleaner" },
    { number: 12, label: "Sewing Machine" },
    { number: 13, label: "Flat Iron" },
    { number: 14, label: "Oven Toaster" },
    { number: 15, label: "Others" }
  ];
  const ictItems = [
    { number: 1, label: "Radio/Tape recorder/Stereo" },
    { number: 2, label: "Television (TV)" },
    { number: 3, label: "VCR/Karaoke/Videoke" },
    { number: 4, label: "Video Games" },
    { number: 5, label: "Computer/Laptop" },
    { number: 6, label: "Printer" },
    { number: 7, label: "CD/VCD/DVD Player" },
    { number: 8, label: "Telephone" },
    { number: 9, label: "Mobile Phone" },
    { number: 10, label: "Tablet" },
  ];
  const vehicleItems = [
    { number: 1, label: "Car" },
    { number: 2, label: "Motorcycle" },
    { number: 3, label: "Bicycle" },
    { number: 4, label: "Tricycle" },
    { number: 5, label: "Boat" },
    { number: 6, label: "Truck" },
    { number: 7, label: "Others" }
  ];

  // Handle dynamic rows for B1
  const handleAddRow = () => {
    const newMobileProviders = [...(formData.mobileProvider || []), ""];
    const newContactNumbers = [...(formData.contactNumber || []), ""];
    onFormChange("mobileProvider", newMobileProviders);
    onFormChange("contactNumber", newContactNumbers);
  };

  const handleRemoveRow = (index) => {
    const newMobileProviders = [...(formData.mobileProvider || [])];
    const newContactNumbers = [...(formData.contactNumber || [])];
    newMobileProviders.splice(index, 1);
    newContactNumbers.splice(index, 1);
    onFormChange("mobileProvider", newMobileProviders);
    onFormChange("contactNumber", newContactNumbers);
  };

  // Handle quantity adjustment for B3
  const handleQuantityChange = (field, number, delta) => {
    const currentQuantity = (formData[field] || {})[number] || 0;
    const newQuantity = Math.max(0, Math.min(10, currentQuantity + delta)); // Limit between 0 and 10
    onFormChange(field, { ...(formData[field] || {}), [number]: newQuantity });
  };

  // Helper: get quantity by label for each asset type
  const getAssetQuantity = (assetsObj, items, item) => {
    // Try by number as string first (for backend/draft), then by label (for loaded data)
    if (assetsObj && typeof assetsObj === "object") {
      if (assetsObj[String(item.number)] !== undefined) return assetsObj[String(item.number)];
      if (assetsObj[item.label] !== undefined) return assetsObj[item.label];
    }
    return 0;
  };

  return (
    <div style={{ padding: "2rem", maxWidth: "1200px", margin: "auto" }}>
      <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1.5rem" }}>
        Step 2: Household Socioeconomic Information
      </h2>

      {/* B1. Telephone/Mobile Service Provider */}
      <h3 style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "1rem" }}>
        B1. Telephone/Mobile Service Provider
      </h3>
      <Dialog open={open} headerText="Enter custom value">
        <div style={{ padding: "1rem" }}>
          <Input
            value={customValue}
            onInput={(e) => setCustomValue(e.target.value)}
            placeholder="Type here..."
          />
          <div style={{ marginTop: "1rem", textAlign: "right" }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button design="Emphasized" onClick={() => handleDialogSubmit(dialogField, dialogFieldOptions)}>OK</Button>
          </div>
        </div>
      </Dialog>
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "1.5rem" }}>
        <tbody>
          {(formData.mobileProvider || []).map((provider, index) => (
            <tr key={index}>
              <td style={{ width: "45%", padding: "8px" }}>
                <Label style={{ fontWeight: "bold", fontSize: "0.9rem" }}>Service Provider</Label>
                <ComboBox
                  value={provider}
                  onChange={(e) => {
                    const newProviders = [...formData.mobileProvider];
                    if (e.target.value === 'Others') {
                      setDialogIndex(index);
                      setOpen(true);
                    } else {
                      newProviders[index] = e.target.value;
                      onFormChange("mobileProvider", newProviders);
                    }
                  }}
                  style={{ width: "100%", fontSize: "0.9rem" }}
                  placeholder="Select Service Provider"
                >
                  {mobileProviderOptions.map((option) => (
                    <ComboBoxItem key={option.value} text={option.label} />
                  ))}
                </ComboBox>
              </td>
              <td style={{ width: "45%", padding: "8px" }}>
                <Label style={{ fontWeight: "bold", fontSize: "0.9rem" }}>Contact Number</Label>
                <Input
                  value={formData.contactNumber?.[index] || ""}
                  onInput={(e) => {
                    const newNumbers = [...formData.contactNumber];
                    newNumbers[index] = e.target.value;
                    onFormChange("contactNumber", newNumbers);
                  }}
                  placeholder="Enter Contact Number"
                  type="tel"
                  style={{ width: "100%", fontSize: "0.9rem" }}
                />
              </td>
              <td style={{ width: "10%", textAlign: "center", padding: "8px" }}>
                <Button
                  design="Negative"
                  onClick={() => handleRemoveRow(index)}
                  style={{ fontSize: "0.9rem" }}
                >
                  Remove
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button design="Emphasized" onClick={handleAddRow} style={{ fontSize: "0.9rem" }}>
        Add New Row
      </Button>

      {/* B2. Internet Service Provider */}
      <h3 style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "1rem" }}>
        B2. Internet Service Provider
      </h3>
      <div style={{ marginBottom: "1.5rem" }}>
        <Label style={{ fontWeight: "bold", fontSize: "0.9rem" }}>Select Internet Provider</Label>
        <select
          value={formData.internetProvider || ""}
          onChange={(e) => onFormChange("internetProvider", e.target.value)}
          style={{ width: "100%", padding: "0.5rem", fontSize: "0.9rem" }}
        >
          <option value="">Select Provider</option>
          {internetProviderOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {formData.internetProvider === "Others" && (
          <div style={{ marginTop: "1rem" }}>
            <Label style={{ fontWeight: "bold", fontSize: "0.9rem" }}>Specify Other Provider</Label>
            <Input
              value={formData.internetProviderOther || ""}
              onInput={(e) => onFormChange("internetProviderOther", e.target.value)}
              placeholder="Specify other provider"
              style={{ width: "100%", fontSize: "0.9rem" }}
            />
          </div>
        )}
      </div>

      {/* B3. Family Assets */}
      <h3 style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "1rem" }}>
        B3. Family Assets
      </h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1.5rem" }}>
        {/* Household Appliances */}
        <div style={{ border: "1px solid #ccc", borderRadius: "8px", padding: "1rem", backgroundColor: "#f9f9f9" }}>
          <h4 style={{ fontSize: "1rem", fontWeight: "bold", marginBottom: "1rem" }}>Household Appliances</h4>
          {applianceItems.map((item) => (
            <div key={item.number} style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
              <Label style={{ fontSize: "0.9rem", flex: 1 }}>{item.number}. {item.label}</Label>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Button
                  design="Transparent"
                  onClick={() => handleQuantityChange("householdAppliances", item.number, -1)}
                  style={{ fontSize: "0.9rem", padding: "0.25rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  -
                </Button>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={getAssetQuantity(formData.householdAppliances, applianceItems, item)}
                  onInput={e => {
                    let val = parseInt(e.target.value, 10);
                    if (isNaN(val)) val = 0;
                    val = Math.max(0, Math.min(10, val));
                    handleQuantityChange("householdAppliances", item.number, val - getAssetQuantity(formData.householdAppliances, applianceItems, item));
                  }}
                  style={{
                    width: "3rem",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    padding: "0.25rem 0.5rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    textAlign: "center"
                  }}
                />
                <Button
                  design="Transparent"
                  onClick={() => handleQuantityChange("householdAppliances", item.number, 1)}
                  style={{ fontSize: "0.9rem", padding: "0.25rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  +
                </Button>
              </div>
            </div>
          ))}
          {Object.keys(appliancesOthers).map((value, index) => {
            const item = appliancesOthers[value];
            return <div key={value} style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
              <Label style={{ fontSize: "0.9rem", flex: 1 }}>{value}.</Label>
              <Input value={item.name} onInput={(e) => onAssetOtherInputChange(setAppliancesOthers, value, 'name', e.target.value.toUpperCase())} style={{width: '100%'}}/>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Button
                  design="Transparent"
                  onClick={() => {
                    const currentQuantity = item.quantity || 0;
                    const newQuantity = Math.max(0, Math.min(10, currentQuantity - 1)); // Limit between 0 and 10
                    onAssetOtherInputChange(setAppliancesOthers, value, 'quantity', newQuantity);
                  }}
                  style={{ fontSize: "0.9rem", padding: "0.25rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  -
                </Button>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={item.quantity}
                  onInput={e => {
                    let val = parseInt(e.target.value, 10);
                    if (isNaN(val)) val = 0;
                    val = Math.max(0, Math.min(10, val));
                    onAssetOtherInputChange(setAppliancesOthers, value, "quantity", val);
                  }}
                  style={{
                    width: "3rem",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    padding: "0.25rem 0.5rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    textAlign: "center"
                  }}
                />
                <Button
                  design="Transparent"
                  onClick={() => {
                    const currentQuantity = item.quantity || 0;
                    const newQuantity = Math.max(0, Math.min(10, currentQuantity + 1)); // Limit between 0 and 10
                    onAssetOtherInputChange(setAppliancesOthers, value, 'quantity', newQuantity);
                  }}
                  style={{ fontSize: "0.9rem", padding: "0.25rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  +
                </Button>
              </div>
            </div>
          })}
          {Object.keys(appliancesOthers).length !== 0 && <Button design="Negative" onClick={() => deleteAssetOther(setAppliancesOthers, appliancesOthers, applianceItems)} style={{ display: "flex", justifyContent: "center", marginBottom: '0.5rem' }}>
            Delete Previous Entry
          </Button>}
          <Button design="Emphasized" onClick={() => addAssetOther(setAppliancesOthers, appliancesOthers, applianceItems)} style={{ display: "flex", justifyContent: "center" }}>
            Add Other Appliance 
          </Button>
        </div>

        {/* ICT Devices */}
        <div style={{ border: "1px solid #ccc", borderRadius: "8px", padding: "1rem", backgroundColor: "#f9f9f9" }}>
          <h4 style={{ fontSize: "1rem", fontWeight: "bold", marginBottom: "1rem" }}>ICT Devices</h4>
          {ictItems.map((item) => (
            <div key={item.number} style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
              <Label style={{ fontSize: "0.9rem", flex: 1 }}>{item.number}. {item.label}</Label>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Button
                  design="Transparent"
                  onClick={() => handleQuantityChange("ictDevices", item.number, -1)}
                  style={{ fontSize: "0.9rem", padding: "0.25rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  -
                </Button>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={getAssetQuantity(formData.ictDevices, ictItems, item)}
                  onInput={e => {
                    let val = parseInt(e.target.value, 10);
                    if (isNaN(val)) val = 0;
                    val = Math.max(0, Math.min(10, val));
                    handleQuantityChange("ictDevices", item.number, val - getAssetQuantity(formData.ictDevices, ictItems, item));
                  }}
                  style={{
                    width: "3rem",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    padding: "0.25rem 0.5rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    textAlign: "center"
                  }}
                />
                <Button
                  design="Transparent"
                  onClick={() => handleQuantityChange("ictDevices", item.number, 1)}
                  style={{ fontSize: "0.9rem", padding: "0.25rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  +
                </Button>
              </div>
            </div>
          ))}
          {Object.keys(ictOthers).map((value, index) => {
            const item = ictOthers[value];
            return <div key={value} style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
              <Label style={{ fontSize: "0.9rem", flex: 1 }}>{value}.</Label>
              <Input value={item.name} onInput={(e) => onAssetOtherInputChange(setIctOthers, value, 'name', e.target.value.toUpperCase())} style={{width: '100%'}}/>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Button
                  design="Transparent"
                  onClick={() => {
                    const currentQuantity = item.quantity || 0;
                    const newQuantity = Math.max(0, Math.min(10, currentQuantity - 1)); // Limit between 0 and 10
                    onAssetOtherInputChange(setIctOthers, value, 'quantity', newQuantity);
                  }}
                  style={{ fontSize: "0.9rem", padding: "0.25rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  -
                </Button>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={item.quantity}
                  onInput={e => {
                    let val = parseInt(e.target.value, 10);
                    if (isNaN(val)) val = 0;
                    val = Math.max(0, Math.min(10, val));
                    onAssetOtherInputChange(setIctOthers, value, "quantity", val);
                  }}
                  style={{
                    width: "3rem",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    padding: "0.25rem 0.5rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    textAlign: "center"
                  }}
                />
                <Button
                  design="Transparent"
                  onClick={() => {
                    const currentQuantity = item.quantity || 0;
                    const newQuantity = Math.max(0, Math.min(10, currentQuantity + 1)); // Limit between 0 and 10
                    onAssetOtherInputChange(setIctOthers, value, 'quantity', newQuantity);
                  }}
                  style={{ fontSize: "0.9rem", padding: "0.25rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  +
                </Button>
              </div>
            </div>
          })}
          {Object.keys(ictOthers).length !== 0 && <Button design="Negative" onClick={() => deleteAssetOther(setIctOthers, ictOthers, ictItems)} style={{ display: "flex", justifyContent: "center", marginBottom: '0.5rem' }}>
            Delete Previous Entry
          </Button>}
          <Button design="Emphasized" onClick={() => addAssetOther(setIctOthers, ictOthers, ictItems)} style={{ display: "flex", justifyContent: "center" }}>
            Add Other ICT Device 
          </Button>
        </div>

        {/* Vehicles */}
        <div style={{ border: "1px solid #ccc", borderRadius: "8px", padding: "1rem", backgroundColor: "#f9f9f9" }}>
          <h4 style={{ fontSize: "1rem", fontWeight: "bold", marginBottom: "1rem" }}>Vehicles</h4>
          {vehicleItems.map((item) => (
            <div key={item.number} style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
              <Label style={{ fontSize: "0.9rem", flex: 1 }}>{item.number}. {item.label}</Label>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Button
                  design="Transparent"
                  onClick={() => handleQuantityChange("vehicles", item.number, -1)}
                  style={{ fontSize: "0.9rem", padding: "0.25rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  -
                </Button>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={getAssetQuantity(formData.vehicles, vehicleItems, item)}
                  onInput={e => {
                    let val = parseInt(e.target.value, 10);
                    if (isNaN(val)) val = 0;
                    val = Math.max(0, Math.min(10, val));
                    handleQuantityChange("vehicles", item.number, val - getAssetQuantity(formData.vehicles, vehicleItems, item));
                  }}
                  style={{
                    width: "3rem",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    padding: "0.25rem 0.5rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    textAlign: "center"
                  }}
                />
                <Button
                  design="Transparent"
                  onClick={() => handleQuantityChange("vehicles", item.number, 1)}
                  style={{ fontSize: "0.9rem", padding: "0.25rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  +
                </Button>
              </div>
            </div>
          ))}
          {Object.keys(vehiclesOthers).map((value, index) => {
            const item = vehiclesOthers[value];
            return <div key={value} style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
              <Label style={{ fontSize: "0.9rem", flex: 1 }}>{value}.</Label>
              <Input value={item.name} onInput={(e) => onAssetOtherInputChange(setVehiclesOthers, value, 'name', e.target.value.toUpperCase())} style={{width: '100%'}}/>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Button
                  design="Transparent"
                  onClick={() => {
                    const currentQuantity = item.quantity || 0;
                    const newQuantity = Math.max(0, Math.min(10, currentQuantity - 1)); // Limit between 0 and 10
                    onAssetOtherInputChange(setVehiclesOthers, value, 'quantity', newQuantity);
                  }}
                  style={{ fontSize: "0.9rem", padding: "0.25rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  -
                </Button>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={item.quantity}
                  onInput={e => {
                    let val = parseInt(e.target.value, 10);
                    if (isNaN(val)) val = 0;
                    val = Math.max(0, Math.min(10, val));
                    onAssetOtherInputChange(setVehiclesOthers, value, "quantity", val);
                  }}
                  style={{
                    width: "3rem",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    padding: "0.25rem 0.5rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    textAlign: "center"
                  }}
                />
                <Button
                  design="Transparent"
                  onClick={() => {
                    const currentQuantity = item.quantity || 0;
                    const newQuantity = Math.max(0, Math.min(10, currentQuantity + 1)); // Limit between 0 and 10
                    onAssetOtherInputChange(setVehiclesOthers, value, 'quantity', newQuantity);
                  }}
                  style={{ fontSize: "0.9rem", padding: "0.25rem 0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  +
                </Button>
              </div>
            </div>
          })}
          {Object.keys(vehiclesOthers).length !== 0 && <Button design="Negative" onClick={() => deleteAssetOther(setVehiclesOthers, vehiclesOthers, vehicleItems)} style={{ display: "flex", justifyContent: "center", marginBottom: '0.5rem' }}>
            Delete Previous Entry
          </Button>}
          <Button design="Emphasized" onClick={() => addAssetOther(setVehiclesOthers, vehiclesOthers, vehicleItems)} style={{ display: "flex", justifyContent: "center" }}>
            Add Other Vehicle
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SocioeconomicForm;